// PATCH 2 — PURE rebuilt-review helpers (no React/DOM) so the authoritative-nutrition rules are
// deterministically testable. A row produced by the rebuild-v2 adapter carries `row.rebuilt`
// (authoritative). Legacy rows (no `row.rebuilt`) keep the existing perUnit x amount math untouched.

const r1 = (n) => Math.round(n * 10) / 10;
const r2 = (n) => Math.round(n * 100) / 100;
const MACROS = ['protein', 'carbs', 'fat'];

export const isRebuiltRow = (row) => Boolean(row && row.rebuilt);

// Authoritative consumed-scope nutrition. Rebuilt row => rebuilt.snapshot (NEVER perUnit x amount).
// Legacy row => perUnit x amount, unchanged.
export const rowSnapshot = (row) => {
  if (isRebuiltRow(row)) {
    const s = row.rebuilt.snapshot || {};
    return { calories: s.calories || 0, protein: s.protein || 0, carbs: s.carbs || 0, fat: s.fat || 0 };
  }
  if (row && row.perUnit) {
    return {
      calories: row.perUnit.calories * row.amount,
      protein: row.perUnit.protein * row.amount,
      carbs: row.perUnit.carbs * row.amount,
      fat: row.perUnit.fat * row.amount,
    };
  }
  return null;
};

export const rowCalories = (row) => { const s = rowSnapshot(row); return s ? Math.round(s.calories) : null; };
export const rowMacro = (row, k) => { const s = rowSnapshot(row); return s ? r1(s[k]) : null; };

// Authoritative displayed physical amount. Rebuilt => rebuilt.amount (value/unit/source/approximate).
export const displayAmount = (row) => {
  if (isRebuiltRow(row)) {
    const a = row.rebuilt.amount || {};
    return { value: a.value, unit: a.unit, source: a.source, approximate: !!a.approximate };
  }
  return { value: row.amount, unit: row.unit, source: row.portion_estimated ? 'estimated' : 'user', approximate: !!row.portion_estimated };
};

// PORTION-estimate treatment comes ONLY from amount provenance (source/approximate) — NEVER from
// nutrition sanity. Legacy falls back to portion_estimated.
export const isEstimatedPortion = (row) => {
  if (isRebuiltRow(row)) { const a = row.rebuilt.amount || {}; return a.source === 'estimated' || !!a.approximate; }
  return !!row.portion_estimated;
};

// Nutrition sanity is a DISTINCT transient review signal (never labelled "estimated portion"). Prefer the
// adapter semantic (row.nutrition_review); also honor the current backend value ("needs_review_confirmation"
// and legacy "needs_review"). Unavailable sanity => not a review (render normally, no manufactured warning).
export const needsNutritionReview = (row) => {
  if (!isRebuiltRow(row)) return false;
  if (row.nutrition_review === true || row.rebuilt.nutrition_review === true) return true;
  const s = row.rebuilt.nutrition_sanity;
  return s === 'needs_review_confirmation' || s === 'needs_review';
};

// A residual remainder is DERIVED — the UI must NOT expose it as an independently rescalable/swappable row.
export const isDerivedRemainder = (row) => isRebuiltRow(row) && (row.rebuilt.derived === true || row.rebuilt.role === 'residual_remainder');
export const isResidualAnchor = (row) => isRebuiltRow(row) && (row.rebuilt.residual_anchor === true || row.rebuilt.role === 'residual_anchor');

// Meal subtotal from AUTHORITATIVE snapshots of the counted rows, consistent with the backend subtotal.
// Excludes anything not passed in (callers exclude non-additive parent context + unresolved foods).
export const subtotalOf = (rows) => {
  const out = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  (rows || []).forEach((row) => { const s = rowSnapshot(row); if (s) MACROS.concat('calories').forEach((k) => { out[k] += s[k] || 0; }); });
  return { calories: Math.round(out.calories), protein: Math.round(out.protein), carbs: Math.round(out.carbs), fat: Math.round(out.fat) };
};

// Reconstruct rebuild-v2 build entries from adapter rows (for the no-DB recompute/confirm endpoints).
// Transient resolved meaning (modifications/add_on/alternates/brand/quantity_source) is carried back so a
// recompute keeps the already-decided meaning; these are NOT persisted into long-term meal history.
export const entriesFromRows = (rows) => (rows || []).filter(isRebuiltRow).map((row) => ({
  id: row.id,
  semantic_identity: row.rebuilt.semantic_identity,
  origin: row.rebuilt.origin,
  role: row.rebuilt.role,
  provider_identity: row.rebuilt.provider_identity || null,
  matched_provider_identity: row.rebuilt.matched_provider_identity || null,
  snapshot: row.rebuilt.snapshot,
  amount: row.rebuilt.amount,
  non_scalable: !!row.rebuilt.non_scalable,
  serving: row.rebuilt.serving || {},
  modifications: row.rebuilt.modifications || [],
  add_on: !!row.rebuilt.add_on,
  alternates: row.rebuilt.alternates || [],
  brand: row.rebuilt.brand || null,
  quantity_source: row.rebuilt.quantity_source || null,
}));

// A rebuilt draft has unresolved foods (amount needed). Confirmation is blocked while any remain, unless
// the user removes/skips them through the normal edit experience.
export const hasBlockingUnresolved = (unresolved) => Array.isArray(unresolved) && unresolved.length > 0;

// ── Round-6 ── Human-readable resolved modifications for the AFFECTED row (§ Issue 1). We surface ONLY
// the already-resolved human text carried on this row (never raw action names / internal metadata), so a
// user can see instructions like "made with almond milk" / "no butter" were understood. Per-row by
// construction => an unrelated row can never inherit another row's modifications. Deduped (case-insensitive).
export const rowModifications = (row) => {
  if (!isRebuiltRow(row)) return [];
  const seen = new Set();
  const out = [];
  (row.rebuilt.modifications || []).forEach((m) => {
    if (!m || typeof m !== 'object') return;
    const t = String(m.text || '').trim();
    if (!t) return;
    const key = t.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(t);
  });
  return out;
};

// ── Round-6 ── LOCAL portion-editor draft helpers (§ Issues 2/3/5). The editor seeds from the
// AUTHORITATIVE amount (displayAmount -> rebuilt.amount), NEVER the legacy compat `row.amount` (which the
// adapter may set to 1 for a non-scalable row). Editing stays purely local until Done; exactly ONE amount
// edit is then sent to rebuild recompute. Pure + side-effect free so the wiring is deterministically testable.
export const seedPortionDraft = (row) => {
  const a = displayAmount(row) || {};
  const value = (a.value != null ? a.value : 1);
  return { value, unit: a.unit || '', text: String(value) };
};

export const adjustPortionDraft = (draft, delta) => {
  const base = Number(draft && draft.value) || 0;
  const next = r2(Math.max(0.25, base + delta));
  return { ...(draft || {}), value: next, text: String(next) };
};

export const setPortionDraftText = (draft, text) => {
  const v = parseFloat(text);
  const value = (!isNaN(v) && v > 0) ? r2(v) : (draft ? draft.value : undefined);
  return { ...(draft || {}), text: String(text), value };
};

export const setPortionDraftUnit = (draft, unit) => ({ ...(draft || {}), unit });

// Adopt a TRUSTED provider serving option into the local draft (§ Issue 4 / F): unit + natural size +
// human description. e.g. current "1 container" + option {size:150, unit:"g", description:"150 g"} ->
// draft becomes 150 g (NOT 1 g). No recompute here; identity/FSID is untouched. The option's macros are
// deliberately NOT used — rebuilt nutrition stays authoritative from the backend snapshot on Done.
export const applyPortionServingOption = (draft, option) => {
  if (!option || typeof option !== 'object') return draft;
  const size = (option.size != null && Number(option.size) > 0) ? r2(Number(option.size)) : (draft ? draft.value : 1);
  return { ...(draft || {}), unit: option.unit, value: size, text: String(size),
           servingDescription: option.description || null,
           // EXPLICIT provider-serving selection intent (transient). Its presence lets the backend
           // distinguish a deliberate serving pick (may establish a new provider basis) from an ordinary
           // quantity edit (preserves the current basis). Never persisted.
           servingSelection: { serving_id: (option.serving_id != null ? option.serving_id : null),
                               unit: option.unit, size } };
};

// The VALIDATED numeric amount from the CURRENT visible text (never a stale previous value). Uses a STRICT
// WHOLE-STRING numeric parse (Number(trimmed)) — NOT parseFloat — so a partial/prefix or exponent-notation
// entry (e.g. "2e3", "2px") can never silently submit a different numeric meaning than what is shown. A
// blank / zero / negative / NaN / non-finite entry yields null, so Done stays disabled and no recompute is sent.
export const portionDraftValue = (draft) => {
  const text = String((draft && draft.text) != null ? draft.text : '').trim();
  if (!text) return null;
  const v = Number(text);
  return (Number.isFinite(v) && v > 0) ? r2(v) : null;
};

// Does the draft differ from the row's authoritative amount? (avoid an unnecessary recompute on Done)
export const portionDraftChanged = (row, draft) => {
  const a = displayAmount(row) || {};
  const v = portionDraftValue(draft);
  if (v === null) return false;
  return !(Number(v) === Number(a.value) && String(draft.unit) === String(a.unit));
};

// The SINGLE authoritative amount edit sent on Done. Uses the VALIDATED current entered value (from the
// visible text) — never a stale draft.value. Carries value/unit + source=user/approximate=false; preserves
// amount.state when present. NEVER carries an identity/provider field — a portion edit must not change the
// resolved food identity.
export const buildPortionEdit = (row, draft) => {
  const cur = (row.rebuilt && row.rebuilt.amount) || {};
  const amount = { value: portionDraftValue(draft), unit: draft.unit || cur.unit, source: 'user', approximate: false };
  if (cur.state != null) amount.state = cur.state;
  const edit = { id: row.id, op: (isResidualAnchor(row) ? 'residual_anchor_amount' : 'amount'), amount };
  // Only an EXPLICIT provider-serving pick carries serving_selection -> backend may (re)establish the
  // provider basis. An ordinary +/- or typed quantity edit omits it -> backend preserves the current basis.
  if (draft && draft.servingSelection) edit.serving_selection = draft.servingSelection;
  return edit;
};

// Submittable only when not already saving (no double-submit), the CURRENT visible text is a finite value
// > 0, and the unit is nonblank. Validation is on the visible text, not a retained previous value.
export const canSubmitPortion = (saving, draft) => (
  !saving && !!draft && portionDraftValue(draft) !== null && String((draft && draft.unit) || '').trim() !== ''
);

// A rebuilt portion sheet must NOT be closed (backdrop / cancel) while a recompute is in flight — the
// in-flight authoritative response must land (success -> close) or fail (keep open) first.
export const canClosePortionSheet = (savingPortion) => !savingPortion;

// TRUSTED provider serving options for a rebuilt row — NEVER a fabricated conversion list. Sourced ONLY
// from the provider serving evidence carried on the row (rebuilt.serving.options). This is populated for a
// provider row AND for an AI row that still carries a matched FatSecret FSID (serving-map fell back to
// Role-A but the food's real servings are retained), so the Review dropdown can offer them.
export const providerServingOptions = (row) => {
  if (!isRebuiltRow(row)) return [];
  const s = row.rebuilt.serving || {};
  return Array.isArray(s.options) ? s.options : [];
};

// Trusted provider options worth OFFERING: any whose representation differs from the row's current
// physical amount/unit. One meaningful alternative (e.g. current "1 container" vs option "150 g") is
// enough to show the picker; a lone option identical to the current amount is redundant and hidden.
export const meaningfulServingOptions = (options, current, forceSelectable) => {
  const opts = Array.isArray(options) ? options : [];
  if (forceSelectable) return opts;                       // AI row w/ matched FSID: picking a real provider
  if (!current) return opts;                              // option is meaningful even if it equals current
  return opts.filter((o) => o && (String(o.unit) !== String(current.unit) || Number(o.size) !== Number(current.value)));
};
